import React from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
} from "@mui/material";
import { MdClose } from "react-icons/md";
import Swal from "sweetalert2";
import {
  importOfacSdnandnonSdn,
  importOthers,
  importUkSanctions,
  importPEP,
  importWatchList,
  importAddress,
  baseUrl,
} from "../../../Api";
import { useNavigate } from "react-router-dom";

const FileUploadModal = ({
  selectedTab,
  fileInputs,
  handleFileChange,
  handleClose,
  tabsData,
  setReload,
  onFileUploadSuccess,
  onFileTypeChange,
}) => {
  const navigate = useNavigate();
  const [content, setContent] = React.useState({
    file_type: tabsData[selectedTab],
    file: "",
  });

  const getAllowedFileNames = (selectedTabOrLabel) => {
    // format date

    // allowed file names for each tab
    const allowedFileNames = {
      OFAC: "sdn",
      UKSanctions: `FCDO_SL_Tue_Feb 27 2025`,
      NONSDN: "cons_prim",
      PEP: `PEP_DDMMYY`,
      "Watch List": "WatchList_DDMMYY",
      Negative: `NEGATIVE`,
    };

    // Normalize to the tab label (some callers pass the index/key, so resolve)
    const tabLabel =
      typeof selectedTabOrLabel === "string"
        ? selectedTabOrLabel
        : tabsData[selectedTabOrLabel];

    if (!tabLabel) return null;

    // Accept common variants (with/without space)
    switch (tabLabel) {
      case "OFAC":
        return allowedFileNames.OFAC;
      case "UK Sanctions":
      case "UKSanctions":
        return allowedFileNames.UKSanctions;
      case "NONSDN":
      case "NON SDN":
        return allowedFileNames.NONSDN;
      case "PEP":
        return allowedFileNames.PEP;
      case "Watch List":
        return allowedFileNames["Watch List"];
      case "Negative":
        return allowedFileNames.Negative;
      case "Address":
        // no strict filename enforced for Address here
        return null;
      default:
        return null;
    }
  };

  //validating the file name - returns null when valid or an error message when invalid
  const validateFileName = (file, selectedTabOrLabel) => {
    if (!file) {
      return "Please upload a file";
    }

    // Resolve the visible tab label
    const tabLabel =
      typeof selectedTabOrLabel === "string"
        ? selectedTabOrLabel
        : tabsData[selectedTabOrLabel];
    const nameWithoutExtension = file.name.split(".").slice(0, -1).join(".");
    // UK Sanctions: required format examples supported:
    // - FCDO_SL_YYYYMMDD.ods  (e.g. FCDO_SL_Tue May 13 2025.ods)
    if (tabLabel === "UK Sanctions" || tabLabel === "UKSanctions") {
      // Match format like FCDO_SL_Tue_Feb 27 2025
      // const ukRegex = /^FCDO_SL_[A-Za-z]{3}_[A-Za-z]{3} \d{1,2} \d{4}\.ods$/;
      const ukRegex = /^FCDO_SL_[A-Za-z]{3}_[A-Za-z]{3} \d{1,2} \d{4}$/i;
      if (!ukRegex.test(nameWithoutExtension)) {
        return "UK Sanctions file must match the required pattern (FCDO_SL_Tue_Feb 27 2025.ods).";
      }
      return null;
    }

    // Negative: required format: Misc_DDMMYY.csv
    if (tabLabel === "Negative") {
      // Use DDMMYY format pattern
      // const negRegex =
      //   /^Misc_(\d{2})(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\.csv$/;
      const negRegex = /^NEGATIVE$/i;
      // if (!negRegex.test(file.name)) {
      //   return "Negative file must match the required pattern (Misc_DDMMYY.csv).";
      // }
      if (!negRegex.test(nameWithoutExtension)) {
        return "Negative file must match the required pattern (NEGATIVE.csv).";
      }
      return null;
    }

    // Handle PEP files format: Misc_DDMMYY.csv
    if (tabLabel === "PEP") {
      // const pepRegex = /^PEP_(\d{2})(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\.csv$/;
      const pepRegex = /^PEP$/i;
      if (!pepRegex.test(nameWithoutExtension)) {
        return "PEP file must match the required pattern (PEP.csv).";
      }
      return null;
    }

    // Handle Watch List files
    if (tabLabel === "Watch List") {
      // const watchListRegex =
      //   /^WatchList_(\d{2})(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\.csv$/;
      const watchListRegex = /^WATCH_LIST$/i;
      if (!watchListRegex.test(nameWithoutExtension)) {
        return "Watch List file must match the required pattern (WATCH_LIST.csv).";
      }
      return null;
    }

    // Fallback: use exact expected name when available
    const expectedName = getAllowedFileNames(selectedTabOrLabel);
    // If an expected format exists for common import tabs but the mapping is missing,
    // surface a clear error so the user/admin can know the required format is not configured.
    const tabsRequiringFormat = [
      "OFAC",
      "UK Sanctions",
      "UKSanctions",
      "NONSDN",
      "NON SDN",
      "PEP",
      "Watch List",
      "Negative",
    ];

    if (typeof expectedName !== "string" || !file.name) {
      return "Unable to validate file name";
    }

    if (nameWithoutExtension.toLowerCase() !== expectedName.toLowerCase()) {
      return `Invalid file name, please enter the file name in '${expectedName}.csv' format`;
    }

    return null;
  };

  const submit = async () => {
    // Helper function to show alerts
    const showAlert = (
      text,
      icon = "warning",
      timer = 1500,
      showConfirmButton = false
    ) => {
      Swal.fire({
        text,
        icon,
        showConfirmButton,
        heightAuto: false,
        timer,
        allowOutsideClick: false,
      });
    };

    // File presence check
    if (!content.file) {
      return showAlert("Please upload file!");
    }

    // File name + extension validation
    // Collect all validation errors and show them together if multiple issues exist
    const errors = [];

    // General filename character validation (skip for UK Sanctions which allow spaces)
    if (
      tabsData[selectedTab] !== "UKSanctions" &&
      tabsData[selectedTab] !== "UK Sanctions"
    ) {
      const validFileNameRegex = /^[a-zA-Z0-9_.-]+$/;
      if (!validFileNameRegex.test(content.file.name)) {
        errors.push(
          "File name can only contain letters, numbers, underscores (_), hyphens (-), and dots (.). Spaces and other special characters are not allowed."
        );
      }
    }

    // File extension validation
    let allowableFormats = [".csv"];
    let uksanctionsAFormats = [".ods"];
    if (
      tabsData[selectedTab] === "UKSanctions" ||
      tabsData[selectedTab] === "UK Sanctions"
    ) {
      allowableFormats = uksanctionsAFormats;
    }

    const extension = content.file.name
      .slice(content.file.name.lastIndexOf("."))
      .toLowerCase();
    if (!allowableFormats.includes(extension)) {
      errors.push(
        `Please upload a file in the following format: ${allowableFormats.join(
          ", "
        )}`
      );
    }

    // Filename exact-format validation (e.g. sdn.csv or dated UK file)
    const nameValidationMessage = validateFileName(
      content.file,
      tabsData[selectedTab]
    );
    if (nameValidationMessage) {
      errors.push(nameValidationMessage);
    }

    if (errors.length > 0) {
      // Show combined error messages in a single alert
      // Escape messages to avoid HTML injection and join with <br/> for line breaks
      const escapeHtml = (str) =>
        String(str)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;");
      const htmlMessage = errors
        .slice(0, 2)
        .map((e) => escapeHtml(e))
        .join("<br/>");

      Swal.fire({
        html: htmlMessage,
        icon: "warning",
        showConfirmButton: true,
        heightAuto: false,
        // timer: 5000,
        allowOutsideClick: false,
      });
      return;
    }

    // Prepare FormData
    const bankCode = localStorage.getItem("bank_code");
    const correlId = localStorage.getItem("correl_id");

    const getEffectiveFileType = () => {
      switch (tabsData[selectedTab]) {
        case "OFAC":
          return "OFAC_SDN";
        case "NONSDN":
          return "OFAC_NON_SDN";
        case "UKSanctions":
          return "UK_Sanctions";
        case "Watch List":
          return "WATCH_LIST";
        case "Address":
          return content.file_type;
        default:
          return tabsData[selectedTab];
      }
    };

    const formData = new FormData();
    formData.append("file_type", getEffectiveFileType());
    formData.append("file", content.file);
    formData.append("bank_code", bankCode);
    formData.append("correl_id", correlId);

    // Show loader
    Swal.fire({
      html: `<img src=${require("../../../assets/loader.gif")} style="width: 50px" alt="loader">`,
      showConfirmButton: false,
      heightAuto: false,
      customClass: { popup: "loader-swal" },
      allowOutsideClick: false,
    });

    try {
      // Select API based on tab
      const apiMap = {
        OFAC: importOfacSdnandnonSdn,
        NONSDN: importOfacSdnandnonSdn,
        UKSanctions: importUkSanctions,
        Address: importAddress,
        Negative: importOthers,
        PEP: importPEP,
        "Watch List": importWatchList,
      };

      const apiFunc = apiMap[tabsData[selectedTab]];
      if (!apiFunc) throw new Error("Unsupported tab");

      const apiData = await apiFunc(formData);
      // let apiData;
      Swal.close();

      // Handle API response
      if (apiData.status === "S") {
        Swal.fire({
          text: "File uploaded successfully!",
          icon: "success",
          showConfirmButton: true,
          heightAuto: false,
        }).then(() => {
          setReload((prev) => !prev);
          onFileUploadSuccess?.();
          handleClose();
        });
      } else if (
        apiData.status === "F" &&
        [
          "Unauthorized - Missing token",
          "Unauthorized - Invalid token",
        ].includes(apiData.message)
      ) {
        showAlert("Please login with your credentials", "warning", 3000);
        localStorage.clear();
        navigate("/login");
      } else if (apiData.result_code === 402) {
        showAlert(
          "No data found in the file. Please check file content.",
          "warning",
          3000
        );
      } else if (apiData.result_code === 405) {
        Swal.fire({
          icon: "warning",
          text:
            apiData.message ||
            "Header is missing or incorrect. Please check the file header.",
          showConfirmButton: true,
          heightAuto: false,
        });
      } else if (apiData.result_code === 400) {
        Swal.fire({
          icon: "warning",
          showConfirmButton: true,
          heightAuto: false,
          html: `
                    <p style="margin-bottom: 8px;">
                      ${
                        apiData.message ||
                        "Invalid File name. Please check file name."
                      }
                    </p>
                   ${
                     apiData.log_file &&
                     `<a href="${baseUrl + "/files/logs/" + apiData.log_file}" 
                       target="_blank" 
                       style="color:#3366ff; text-decoration: underline;" download="${
                         apiData.log_file
                       }">
                      Download Log File
                    </a>`
                   }
               `,
        });
      } else {
        showAlert(apiData?.message || "Error uploading file", "error", 3000);
      }
    } catch (error) {
      console.error(error);
      showAlert(
        "Something went wrong, please try again later.",
        "error",
        undefined,
        true
      );
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="row justify-content-center align-items-center">
          <div className="col-12 d-flex justify-content-between align-items-center">
            <h5 className="mt-4">Upload {tabsData[selectedTab]} File</h5>
            <button className="border-0 bg-transparent" onClick={handleClose}>
              <MdClose color="black" fontSize={20} />
            </button>
          </div>
        </div>
        {fileInputs.map(({ id, files }, index) => (
          <React.Fragment key={id}>
            {/* Address file type selection commented out */}
            {tabsData[selectedTab] === "Address" && (
              <div className="col-6 mt-3">
                <FormControl size="small">
                  <InputLabel>File Type</InputLabel>
                  <Select
                    label="File Type"
                    disableUnderline
                    sx={{ minWidth: 400 }}
                    value={content.file_type}
                    onChange={(e) => {
                      const selectedFileType = e.target.value;
                      setContent((prev) => ({
                        ...prev,
                        file_type: selectedFileType,
                      }));
                      // Call the callback to update parent component
                      if (onFileTypeChange) {
                        onFileTypeChange(selectedFileType);
                      }
                    }}
                  >
                    <MenuItem value="OFAC_ADD_SDN">
                      <ListItemText primary="SDN" />
                    </MenuItem>
                    <MenuItem value="OFAC_ADD_NON_SDN">
                      <ListItemText primary="NON SDN" />
                    </MenuItem>
                  </Select>
                </FormControl>
              </div>
            )}

            <div className="row justify-content-between align-items-center mb-3">
              <div className="col-12">
                <div className="choose-file-input d-flex align-items-center">
                  <label
                    className="btn choose-file-btn me-2"
                    htmlFor={`fileInput-${id}`}
                  >
                    Choose Files
                  </label>
                  <input
                    id={`fileInput-${id}`}
                    type="file"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const files = e.target.files;
                      if (!files || files.length === 0) {
                        console.error("No file selected");
                        return;
                      }
                      const file = files[0];
                      handleFileChange(id, e);
                      setContent((prev) => ({
                        ...prev,
                        file,
                      }));
                    }}
                    accept={
                      tabsData[selectedTab] === "UK Sanctions"
                        ? ".ods" // Accept only .ods files for UK Sanctions tab
                        : ".csv, .ods" // Default formats for other tabs
                    }
                  />
                  <p
                    className="mb-0 choose-file-name flex-grow-1"
                    onClick={() =>
                      document.getElementById(`fileInput-${id}`).click()
                    }
                  >
                    {files?.name || "No file chosen"}
                  </p>
                </div>
              </div>
            </div>
          </React.Fragment>
        ))}
        <div className="d-flex justify-content-center mt-3 gap-3">
          <button
            className="btn createButton"
            onClick={submit}
            disabled={!content.file}
          >
            Upload
          </button>
          <button className="btn cancelButton" onClick={handleClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default FileUploadModal;
